import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CalendarClock, Send, RefreshCw, Loader2, Mail } from "lucide-react";
import { API, useAuth } from "../../App";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—");

export const ReferralUpdateAutomation = ({ companyId }) => {
  const { token, user } = useAuth();
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const load = () =>
    axios.get(`${API}/partners/automation/referral-update`, { headers: authHeader })
      .then((r) => { setStatus(r.data); setError(""); })
      .catch((e) => setError(e?.response?.data?.detail || e.message));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async (kind) => {
    setBusy(kind);
    const params = kind === "test" ? { test_email: user?.email } : kind === "resend" ? { resend: true } : {};
    try {
      const r = await axios.post(`${API}/partners/companies/${companyId}/send-digest`, {}, { headers: authHeader, params });
      const { sent, skipped, period, capped_by_daily_limit: capped } = r.data;
      const ok = sent > 0;
      toast({
        title: kind === "test"
          ? (ok ? `Test sent to ${user?.email}` : "Test email could not be delivered")
          : ok ? `Monthly update sent to ${sent} contact${sent === 1 ? "" : "s"}` : "Nothing sent",
        description: `${period?.label || ""}${skipped ? ` · ${skipped} skipped (already sent this period or delivery failed)` : ""}${capped ? ` · ${capped} held for tomorrow (daily limit reached)` : ""}`,
        variant: ok ? undefined : "destructive",
      });
      load();
    } catch (e) {
      toast({ title: "Send failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally { setBusy(""); }
  };

  const control = async (path, params = {}) => {
    setBusy(path);
    try {
      const r = await axios.post(`${API}/partners/automation/referral-update/${path}`, {}, { headers: authHeader, params });
      if (path === "run") toast({ title: r.data.paused ? "Automation is paused — nothing sent" : `Batch run: ${r.data.sent} sent, ${r.data.skipped} already sent`, variant: r.data.paused ? "destructive" : undefined });
      load();
    } catch (e) {
      toast({ title: "Action failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally { setBusy(""); }
  };
  const cycle = status?.cycle;

  const companyDeliveries = (status?.recent_deliveries || []).filter((d) => !companyId || d.company_id === companyId).slice(0, 5);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="referral-update-automation">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-[#1F2A44] flex items-center gap-2"><CalendarClock className="w-4 h-4 text-[#C8A96B]" /> Referral Update Automation</h3>
          <p className="text-sm text-slate-600 mt-1">
            Frequency: <span className="font-semibold text-[#1F2A44]" data-testid="referral-update-frequency">{status ? status.frequency.charAt(0).toUpperCase() + status.frequency.slice(1) : "…"}</span>
            {status && <span className="text-slate-400"> · {status.schedule}</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {status && (
            <Button size="sm" variant={status.paused ? "default" : "outline"} onClick={() => control("pause", { paused: !status.paused })} disabled={!!busy} data-testid="referral-update-pause-btn">
              {status.paused ? "Resume automation" : "Pause automation"}
            </Button>
          )}
          {status && !status.paused && (
            <Button size="sm" variant="outline" onClick={() => control("run")} disabled={!!busy} data-testid="referral-update-run-btn" title="Run today's batch now (respects the daily send limit)">
              {busy === "run" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Run today's batch"}
            </Button>
          )}
        {companyId && (
          <>
            <Button size="sm" variant="outline" onClick={() => send("test")} disabled={!!busy} data-testid="referral-update-test-btn">
              {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-1" /> Send Test to Me</>}
            </Button>
            <Button size="sm" variant="outline" onClick={() => send("send")} disabled={!!busy} data-testid="partner-send-digest-btn">
              {busy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Send Monthly Update</>}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => send("resend")} disabled={!!busy} data-testid="referral-update-resend-btn" title="Bypass the once-per-period guard">
              {busy === "resend" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1" /> Resend</>}
            </Button>
          </>
        )}
        </div>
      </div>
      {cycle && (
        <div className="mt-4 rounded-lg border border-[#C8A96B]/40 bg-[#C8A96B]/10 p-3 text-sm" data-testid="referral-update-cycle">
          <p className="font-semibold text-[#1F2A44]">Monthly Referral Update — {cycle.period_label}
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${cycle.status === "Complete" ? "bg-emerald-100 text-emerald-800" : cycle.status === "Paused" ? "bg-amber-100 text-amber-800" : "bg-[#0A2342] text-white"}`} data-testid="referral-update-cycle-status">{cycle.status}</span>
          </p>
          <p className="text-slate-700 mt-1" data-testid="referral-update-cycle-stats">
            Sent this month: <b>{cycle.sent}</b> · Remaining: <b>{cycle.remaining}</b> · Today's sends: <b>{status.today_sends} / {status.daily_limit}</b> · Daily limit: <b>{status.daily_limit}</b>
          </p>
          {status.previous_cycle_unfinished > 0 && (
            <p className="text-amber-700 mt-1" data-testid="referral-update-prev-unfinished">⚠ {status.previous_cycle_unfinished} contact(s) from {status.previous_cycle} never received that cycle's update — flagged, not auto-resent.</p>
          )}
        </div>
      )}
      {error && <p className="mt-3 text-sm text-red-600" data-testid="referral-update-error">{error}</p>}
      {status && (
        <dl className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div className="rounded-lg bg-slate-50 p-3"><dt className="text-xs uppercase tracking-wide text-slate-500">Next scheduled send</dt><dd className="font-semibold text-[#1F2A44]" data-testid="referral-update-next">{fmt(status.next_scheduled_send)}</dd></div>
          <div className="rounded-lg bg-slate-50 p-3"><dt className="text-xs uppercase tracking-wide text-slate-500">Last successful send</dt><dd className="font-semibold text-[#1F2A44]" data-testid="referral-update-last">{fmt(status.last_successful_send)}</dd></div>
          <div className="rounded-lg bg-slate-50 p-3"><dt className="text-xs uppercase tracking-wide text-slate-500">Current reporting period</dt><dd className="font-semibold text-[#1F2A44]" data-testid="referral-update-period">{status.current_reporting_period?.label}</dd></div>
        </dl>
      )}
      {status && (
        <div className="mt-4" data-testid="referral-update-deliveries">
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Delivery status</p>
          {companyDeliveries.length === 0 ? (
            <p className="text-sm text-slate-500">No sends recorded yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {companyDeliveries.map((d) => (
                <li key={d.id} className="py-1.5 flex flex-wrap justify-between gap-2 text-sm">
                  <span className="text-slate-700 truncate">{d.email} <span className="text-slate-400">· {d.period_label || d.period_key || "—"} · {d.trigger}</span></span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${d.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{d.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
};
